<template>
	<div v-if="summary" class="checkout-billing mb-5" :class="{'checkout-billing--annual': newBillingPipeline}">
		<h3 v-if="collectTaxId" class="font-display text-4 mb-3">Organization Information</h3>
		<CSummaryBlock
			v-if="collectTaxId && showOrgSummary"
			data-testid="tax-info-summary"
			edit-title="Edit organization information"
			class="mb-4"
			@edit="editingOrgInfo = true"
		>
			<p>
				<template v-if="billingInfo.OrganizationType">{{ billingInfo.OrganizationType == 'Business' ? 'Business' : 'Personal' }}<br></template>
				<template v-if="billingInfo.Company">{{ billingInfo.Company }}</template>
			</p>
		</CSummaryBlock>
		<CBusinessInformation
			v-else-if="collectTaxId"
			:organization-type="subscriptionViewModelInternal.BillingInfo.OrganizationType"
			:business="subscriptionViewModelInternal.BillingInfo.Company"
			:tax-ids="subscriptionViewModelInternal.BillingInfo.TaxIds"
			:country-code="subscriptionViewModelInternal.BillingInfo.CountryCode"
			:state="subscriptionViewModelInternal.BillingInfo.State"
			:postal-code="subscriptionViewModelInternal.BillingInfo.PostalCode"
			:tax-id-invalid="taxIdInvalid"
			:purchase-order-number="subscriptionViewModelInternal.BillingInfo.PurchaseOrderNumber"
			:payment-method="subscriptionViewModelInternal.PaymentMethod"
			:is-v-a-t-registration-opt-out="subscriptionViewModelInternal.BillingInfo.IsVATRegistrationOptOut"
			:show-tax-fields="false"
			class="mb-4"
			@reset-tax-id-invalid="taxIdInvalid = false"
			@update:organization-type="handleFieldChange('OrganizationType', $event)"
			@update:business="handleFieldChange('Company', $event)"
			@update:tax-id="handleFieldChange('TaxIds', $event)"
			@update:purchase-order-number="handleFieldChange('PurchaseOrderNumber', $event)"
			@update:is-vat-registration-opt-out="handleFieldChange('IsVATRegistrationOptOut', $event)"
		/>

		<h3 class="font-display text-4 mb-3">Billing Information</h3>
		<CSummaryBlock
			v-if="showBillingSummary"
			data-testid="billing-info-summary"
			edit-title="Edit billing information"
			class="mb-4"
			@edit="editingBillingInfo = true"
		>
			<p>
				{{ billingInfo.FirstAndLast }}<br>
				{{ billingInfo.EmailAddress }}<br>
				<template v-if="billingInfo.Company && !collectTaxId">{{ billingInfo.Company }}<br></template>
				{{ billingInfo.Line1 }}<br>
				<template v-if="billingInfo.Line2">{{ billingInfo.Line2 }}<br></template>
				{{ billingInfo.CityStatePostalCode }}<br>
				{{ billingInfo.Country }}
				<template v-if="collectTaxId">
					<span v-for="taxId in displayedTaxIds" :key="taxId.name" class="block mt-3">{{ taxId.name }}: {{ taxId.id }}</span>
					<template v-if="billingInfo.PurchaseOrderNumber"><span class="block">PO Number: {{ billingInfo.PurchaseOrderNumber }}</span></template>
				</template>
			</p>
		</CSummaryBlock>
		<div v-else class="mb-4">
			<div class="flex gap-2">
				<CValidatedInput
					vid="first-name"
					:value="subscriptionViewModelInternal.BillingInfo.FirstName || ''"
					label="First Name"
					name="First Name"
					type="text"
					rules="required"
					class="w-50"
					@change="handleFieldChange('FirstName', $event)"
				/>
				<CValidatedInput
					vid="last-name"
					:value="subscriptionViewModelInternal.BillingInfo.LastName || ''"
					label="Last Name"
					name="Last Name"
					type="text"
					immediate
					rules="required"
					class="w-50"
					@change="handleFieldChange('LastName', $event)"
				/>
			</div>
			<CValidatedInput
				vid="email"
				:value="subscriptionViewModelInternal.BillingInfo.EmailAddress || ''"
				label="Email Address"
				name="Email Address"
				type="email"
				rules="required|email"
				:class="{'b5:w-50 b5:pr-2': newBillingPipeline}"
				@change="handleFieldChange('EmailAddress', $event)"
			/>
			<CValidatedInput
				v-if="!collectTaxId"
				vid="company"
				:value="subscriptionViewModelInternal.BillingInfo.Company || ''"
				label="Company (optional)"
				placeholder="Company"
				name="Company"
				type="text"
				rules=""
				@change="handleFieldChange('Company', $event)"
			/>
			<CAddress
				:address="subscriptionViewModelInternal.BillingInfo"
				:disable-client-side-validation="isCustomerAddressValidationEnabled"
				@change="handleFieldChange"
				@blur="handleAddressFieldBlur"
			/>
			<CBusinessTaxFields
				v-if="collectTaxId"
				:organization-type="subscriptionViewModelInternal.BillingInfo.OrganizationType"
				:tax-ids="subscriptionViewModelInternal.BillingInfo.TaxIds"
				:country-code="subscriptionViewModelInternal.BillingInfo.CountryCode"
				:state="subscriptionViewModelInternal.BillingInfo.State"
				:postal-code="subscriptionViewModelInternal.BillingInfo.PostalCode"
				:tax-id-invalid="taxIdInvalid"
				:purchase-order-number="subscriptionViewModelInternal.BillingInfo.PurchaseOrderNumber"
				:is-v-a-t-registration-opt-out="subscriptionViewModelInternal.BillingInfo.IsVATRegistrationOptOut"
				@reset-tax-id-invalid="taxIdInvalid = false"
				@update:tax-id="handleFieldChange('TaxIds', $event)"
				@update:purchase-order-number="handleFieldChange('PurchaseOrderNumber', $event)"
				@update:is-vat-registration-opt-out="handleFieldChange('IsVATRegistrationOptOut', $event)"
			/>
		</div>

		<h3 class="font-display text-4 mb-3">Payment Method</h3>
		<div v-if="showPaymentSummary" data-testid="payment-method-summary">
			<CSummaryBlock edit-title="Edit payment method" @edit="editingPayment = true">
				<CPaymentMethod
					:brand="billingInfo.CreditCardBrand"
					:number="billingInfo.LastFourDigits"
					:square-icon="false"
				/>
			</CSummaryBlock>
		</div>
		<CPaymentInformation
			v-show="!showPaymentSummary"
			ref="paymentInformation"
			:card-error-message="cardErrorMessage"
			:select-plan-url="selectPlanUrl"
			:payment-method="subscriptionViewModelInternal.PaymentMethod"
			:card-on-file="cardOnFile"
			:show-prepayment-payment-methods="showPrepaymentPaymentMethods"
			:stripe-key="stripeKey"
			:po-number="subscriptionViewModelInternal.PONumber"
			:new-plan="subscriptionViewModelInternal.NewPlan"
			:existing-card-info="existingCardInfo"
			:collect-tax-id="collectTaxId"
			:show-header-and-invoice-option="!newBillingPipeline"
			@update:po-number="subscriptionViewModelInternal.PONumber = $event"
			@card-changed="handleCardChange"
			@update:payment-method="subscriptionViewModelInternal.PaymentMethod = $event"
		/>

		<CConfirmAddressDialog
			:model="confirmAddressModel"
			@cancel="confirmAddressDialogCanceled"
			@confirm="confirmAddressDialogConfirmed"
		/>
	</div>
	<div v-else class="grid b7:grid-cols-2 items-start gap-x-4 mb-5">
		<div class="flex gap-2">
			<CValidatedInput
				vid="first-name"
				:value="subscriptionViewModelInternal.BillingInfo.FirstName || ''"
				label="First Name"
				placeholder="First Name"
				name="First Name"
				type="text"
				rules="required"
				class="w-50"
				@change="handleFieldChange('FirstName', $event)"
			/>
			<CValidatedInput
				vid="last-name"
				:value="subscriptionViewModelInternal.BillingInfo.LastName || ''"
				label="Last Name"
				placeholder="Last Name"
				name="Last Name"
				type="text"
				immediate
				rules="required"
				class="w-50"
				@change="handleFieldChange('LastName', $event)"
			/>
		</div>
		<CValidatedInput
			vid="email"
			:value="subscriptionViewModelInternal.BillingInfo.EmailAddress || ''"
			label="Email Address"
			placeholder="Email Address"
			name="Email Address"
			type="email"
			rules="required|email"
			@change="handleFieldChange('EmailAddress', $event)"
		/>
		<CValidatedInput
			v-if="!collectTaxId"
			vid="company"
			:value="subscriptionViewModelInternal.BillingInfo.Company || ''"
			label="Company (optional)"
			placeholder="Company"
			name="Company"
			type="text"
			rules=""
			@change="handleFieldChange('Company', $event)"
		/>
		<div class="col-start-1">
			<CAddress
				:address="subscriptionViewModelInternal.BillingInfo"
				:disable-client-side-validation="isCustomerAddressValidationEnabled"
				@change="handleFieldChange"
				@blur="handleAddressFieldBlur"
			/>

			<CPaymentInformation
				v-if="collectTaxId"
				ref="paymentInformation"
				:card-error-message="cardErrorMessage"
				:select-plan-url="selectPlanUrl"
				:payment-method="subscriptionViewModelInternal.PaymentMethod"
				:card-on-file="cardOnFile"
				:show-prepayment-payment-methods="showPrepaymentPaymentMethods"
				:stripe-key="stripeKey"
				:po-number="subscriptionViewModelInternal.PONumber"
				:new-plan="subscriptionViewModelInternal.NewPlan"
				:existing-card-info="existingCardInfo"
				:collect-tax-id="true"
				:show-header-and-invoice-option="!newBillingPipeline"
				@update:po-number="subscriptionViewModelInternal.PONumber = $event"
				@card-changed="handleCardChange"
				@update:payment-method="subscriptionViewModelInternal.PaymentMethod = $event"
			/>
		</div>

		<CBusinessInformation
			v-if="collectTaxId"
			:organization-type="subscriptionViewModelInternal.BillingInfo.OrganizationType"
			:business="subscriptionViewModelInternal.BillingInfo.Company"
			:tax-ids="subscriptionViewModelInternal.BillingInfo.TaxIds"
			:country-code="subscriptionViewModelInternal.BillingInfo.CountryCode"
			:state="subscriptionViewModelInternal.BillingInfo.State"
			:postal-code="subscriptionViewModelInternal.BillingInfo.PostalCode"
			:tax-id-invalid="taxIdInvalid"
			:purchase-order-number="subscriptionViewModelInternal.BillingInfo.PurchaseOrderNumber"
			:payment-method="subscriptionViewModelInternal.PaymentMethod"
			:is-v-a-t-registration-opt-out="subscriptionViewModelInternal.BillingInfo.IsVATRegistrationOptOut"
			@reset-tax-id-invalid="taxIdInvalid = false"
			@update:organization-type="handleFieldChange('OrganizationType', $event)"
			@update:business="handleFieldChange('Company', $event)"
			@update:tax-id="handleFieldChange('TaxIds', $event)"
			@update:purchase-order-number="handleFieldChange('PurchaseOrderNumber', $event)"
			@update:is-vat-registration-opt-out="handleFieldChange('IsVATRegistrationOptOut', $event)"
		/>

		<CPaymentInformation
			v-if="!collectTaxId"
			ref="paymentInformation"
			:card-error-message="cardErrorMessage"
			:select-plan-url="selectPlanUrl"
			:payment-method="subscriptionViewModelInternal.PaymentMethod"
			:card-on-file="cardOnFile"
			:show-prepayment-payment-methods="showPrepaymentPaymentMethods"
			:stripe-key="stripeKey"
			:po-number="subscriptionViewModelInternal.PONumber"
			:new-plan="subscriptionViewModelInternal.NewPlan"
			:existing-card-info="existingCardInfo"
			:collect-tax-id="false"
			:show-header-and-invoice-option="!newBillingPipeline"
			@update:po-number="subscriptionViewModelInternal.PONumber = $event"
			@card-changed="handleCardChange"
			@update:payment-method="subscriptionViewModelInternal.PaymentMethod = $event"
		/>

		<CConfirmAddressDialog
			:model="confirmAddressModel"
			@cancel="confirmAddressDialogCanceled"
			@confirm="confirmAddressDialogConfirmed"
		/>
	</div>
</template>

<script lang="ts">
	import { computed, defineComponent, type PropType, ref, watch } from 'vue';
	import type { BillingInfo } from '@cognitoforms/api/services/settings/organization-settings-service';
	import type { SubscriptionSettingsViewModel } from '@cognitoforms/api/services/subscription-service';
	import { updateBillingInfo } from '@cognitoforms/api/services/settings/organization-settings-service';
	import CValidatedInput from 'src/components/form-controls/ValidatedInput.vue';
	import notify from 'src/utilities/notify';
	import CAddress from 'src/components/app/Address.vue';
	import CBusinessInformation from 'src/views/settings/organization/BusinessInformation.vue';
	import CBusinessTaxFields from 'src/views/settings/organization/BusinessTaxFields.vue';
	import CPaymentInformation from 'src/views/settings/organization/PaymentInformation.vue';
	import CConfirmAddressDialog from 'src/views/settings/organization/ConfirmAddressDialog.vue';
	import CSummaryBlock from 'src/components/app/SummaryBlock.vue';
	import { getTaxIdConfiguration } from 'src/utilities/tax-id/tax-id-configuration';
	import type { BillingAddressConfirmModel } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/billing-address-confirm-model';
	import type { Address } from '@cognitoforms/types/server-types/model/address';
	import session from '@cognitoforms/api/services/session';
	import CPaymentMethod from 'src/views/entries/PaymentMethod.vue';

	export default defineComponent({
		components: {
			CConfirmAddressDialog,
			CValidatedInput,
			CAddress,
			CBusinessInformation,
			CBusinessTaxFields,
			CPaymentInformation,
			CSummaryBlock,
			CPaymentMethod
		},
		props: {
			stripeKey: {
				type: String, required: true
			},
			subscriptionViewModel: {
				type: Object as PropType<SubscriptionSettingsViewModel>, required: true
			},
			errorMessage: {
				type: String, required: true
			},
			// New billing UI's "summary" view - stacked layout with compact blocks
			summary: {
				type: Boolean, default: false
			}
		},
		emits: {
			'upgrade-ready': (payload: SubscriptionSettingsViewModel) => payload != null,
			'address-updated': () => true,
			'update-payment-method': (value: string) => value != null,
			'set-errors': (errors: Record<string, string[]> | null) => errors,
			'submit': (shouldValidateAddress: boolean) => shouldValidateAddress,
			'tax-info-updated': (updateViewModel: SubscriptionSettingsViewModel) => true
		},
		setup(props, { emit }) {
			let stripeElement;
			const cardOnFile = ref(false);
			const billingInfoChanged = ref(false);
			const existingCardInfo = {
				cardNumberPlaceholder: '',
				cardExpiryPlaceholder: '',
				cardCvcPlaceholder: ''
			};

			const cardErrorMessage = ref(props.errorMessage);
			watch(() => props.errorMessage, (newValue) => { cardErrorMessage.value = newValue; });

			const subscriptionViewModelInternal = ref(props.subscriptionViewModel);
			watch(props.subscriptionViewModel, (newValue) => {
				subscriptionViewModelInternal.value = newValue;
			});

			const confirmAddressModel = ref<BillingAddressConfirmModel | null>(null);
			if (subscriptionViewModelInternal.value.BillingInfo.CreditCardReferenceId?.trim()) {
				cardOnFile.value = true;
				existingCardInfo.cardNumberPlaceholder = subscriptionViewModelInternal.value.BillingInfo.CreditCardInfo;
				existingCardInfo.cardExpiryPlaceholder = subscriptionViewModelInternal.value.BillingInfo.ExpirationDateDisplayValue;
				existingCardInfo.cardCvcPlaceholder = '***';
			}

			// Default payment method should be credit card
			subscriptionViewModelInternal.value.PaymentMethod = 'creditcard';
			watch(() => subscriptionViewModelInternal.value.PaymentMethod, (newVal) => {
				emit('update-payment-method', newVal);
				cardErrorMessage.value = '';
			});

			const getBillingAddress = (info: BillingInfo) => { return { Line1: info.Line1, Line2: info.Line2, City: info.City, State: info.State, PostalCode: info.PostalCode, Country: info.Country };};

			const billingInfo = computed(() => subscriptionViewModelInternal.value.BillingInfo);

			const hasExistingBillingInfo = !!(props.subscriptionViewModel.BillingInfo.FirstName && props.subscriptionViewModel.BillingInfo.Line1);

			const editingBillingInfo = ref(false);
			const editingOrgInfo = ref(false);
			const editingPayment = ref(!cardOnFile.value);
			const taxIdInvalid = ref(false);

			watch(cardErrorMessage, (newValue) => {
				if (newValue)
					editingPayment.value = true;
			});
			watch(taxIdInvalid, (newValue) => {
				if (newValue)
					editingBillingInfo.value = true;
			});

			const requiredTaxIdsSatisfied = computed(() => {
				const info = subscriptionViewModelInternal.value.BillingInfo;
				if (info.OrganizationType !== 'Business' || info.IsVATRegistrationOptOut)
					return true;

				const requiredTypes = getTaxIdConfiguration(info.CountryCode, info.State, info.PostalCode)
					.filter(option => option.required);
				if (requiredTypes.length === 0)
					return true;

				return requiredTypes.every(option => info.TaxIds?.some(taxId => taxId.Type === option.type && taxId.Id));
			});

			const showBillingSummary = computed(() => hasExistingBillingInfo && !editingBillingInfo.value && (!session.value?.enabledFeatures.CollectTaxId || (!taxIdInvalid.value && requiredTaxIdsSatisfied.value)));
			const showOrgSummary = computed(() => hasExistingBillingInfo && !editingOrgInfo.value);
			const showPaymentSummary = computed(() => cardOnFile.value && !editingPayment.value);

			const displayedTaxIds = computed(() => {
				const info = subscriptionViewModelInternal.value.BillingInfo;
				if (!info?.TaxIds?.length)
					return [];

				const taxOptions = getTaxIdConfiguration(info.CountryCode, info.State, info.PostalCode);
				return info.TaxIds
					.map(taxId => ({
						name: taxOptions.find(option => option.type === taxId.Type)?.name ?? 'Tax ID',
						id: taxId.Id
					}))
					.filter(item => item.id);
			});

			return {
				billingInfo,
				editingBillingInfo,
				editingOrgInfo,
				editingPayment,
				showBillingSummary,
				showOrgSummary,
				showPaymentSummary,
				displayedTaxIds,
				cardErrorMessage,
				cardModified: ref(false),
				taxIdInvalid,
				billingInfoChanged,
				cardOnFile,
				existingCardInfo,
				stripeElement,
				latestCostAddress: ref(getBillingAddress(subscriptionViewModelInternal.value.BillingInfo as BillingInfo)),
				showPrepaymentPaymentMethods: props.subscriptionViewModel.PrepaymentMonths > 0,
				subscriptionViewModelInternal,
				collectTaxId: computed(() => session.value?.enabledFeatures.CollectTaxId),
				newBillingPipeline: computed(() => session.value?.enabledFeatures.NewBillingPipeline),
				confirmAddressModel,
				confirmAddressDialogCanceled() {
					confirmAddressModel.value = null;
					notify('Failed to update billing info.', 'error');
					emit('set-errors', null);
				},
				confirmAddressDialogConfirmed(address: Address) {
					const billingInfo = subscriptionViewModelInternal.value.BillingInfo;
					billingInfo.Line1 = address.Line1;
					billingInfo.Line2 = address.Line2;
					billingInfo.City = address.City;
					billingInfo.State = address.State;
					billingInfo.PostalCode = address.PostalCode;
					billingInfo.Country = address.Country;
					billingInfo.CountryCode = address.CountryCode;
					billingInfoChanged.value = true;
					emit('submit', false);
					confirmAddressModel.value = null;
				},
				isCustomerAddressValidationEnabled: computed(() => session.value?.enabledFeatures?.CustomerAddressValidation)
			};
		},
		computed: {
			selectPlanUrl() {
				return `/${this.subscriptionViewModel.OrgCode}/settings/plan/select?prepayplan=${this.subscriptionViewModel.NewPlanLevel}&source=${this.subscriptionViewModel.Source}&details=${this.subscriptionViewModel.Details}&paymentMethod=invoice#prepay`;
			},
			billingAddressEdited() {
				const newBillingInfo = this.subscriptionViewModelInternal.BillingInfo;
				return !(
					newBillingInfo.Line1 === this.latestCostAddress.Line1 &&
					newBillingInfo.Line2 === this.latestCostAddress.Line2 &&
					newBillingInfo.City === this.latestCostAddress.City &&
					newBillingInfo.State === this.latestCostAddress.State &&
					newBillingInfo.PostalCode === this.latestCostAddress.PostalCode &&
					newBillingInfo.Country === this.latestCostAddress.Country
				);
			}
		},
		mounted() {
			this.stripeElement = (this.$refs.paymentInformation as any).$refs.stripeCard;
		},
		methods: {
			notify,
			validateBillingFields() {
				this.cardErrorMessage = '';

				if (!this.cardOnFile && this.subscriptionViewModelInternal.PaymentMethod === 'creditcard') {
					if (this.stripeElement && this.stripeElement.emptyFields) {
						if (this.stripeElement.emptyFields.cardNumber)
							this.cardErrorMessage = 'Card number is required.';
						else if (this.stripeElement.emptyFields.cardExpiry)
							this.cardErrorMessage = 'Expiration date is required.';
						else if (this.stripeElement.emptyFields.cardCvc)
							this.cardErrorMessage = 'CVV is required.';
					}
				}

				return !this.cardErrorMessage;
			},
			handleCardChange(changed: boolean) {
				this.cardModified = changed;
				this.cardErrorMessage = '';
			},
			handleFieldChange(field: string, value: string) {
				if (this.subscriptionViewModelInternal.BillingInfo[field] !== value) {
					this.billingInfoChanged = true;
					this.subscriptionViewModelInternal.BillingInfo[field] = value;

					// Trigger cost recalculation when tax info changes
					if (field === 'OrganizationType' || field === 'TaxIds') {
						this.$emit('tax-info-updated', this.subscriptionViewModelInternal);
					}
				}
			},
			async updateBillingInfo(shouldValidateAddress = true) {
				// Stripe payment method created, update card on file
				let updateBillingSuccess = false;
				try {
					const billingInfo = this.subscriptionViewModelInternal.BillingInfo;

					const response = await updateBillingInfo(billingInfo, this.subscriptionViewModel.SubmissionToken, shouldValidateAddress, true);
					if (!('Type' in response)) {
						updateBillingSuccess = true;

						Object.assign(this.subscriptionViewModel, response);
						this.cardModified = false;

						if (response.BillingInfo.CreditCardReferenceId?.trim()) {
							this.cardOnFile = true;
							this.existingCardInfo.cardNumberPlaceholder = response.BillingInfo.CreditCardInfo;
							this.existingCardInfo.cardExpiryPlaceholder = response.BillingInfo.ExpirationDateDisplayValue;
							this.existingCardInfo.cardCvcPlaceholder = '***';
							this.stripeElement.prefillData();
						}
					}
					// Card requires authentication, prompt authentication through Stripe
					else {
						switch (response.Type) {
						case 'SetupPaymentMethodError':
							if (response.SetupIntentId && response.ClientSecret) {
								billingInfo.AuthorizationTokenText = response.SetupIntentId;
								billingInfo.AuthorizationTokenType = 'SetupIntent';
								billingInfo.AuthorizationTokenClientSecret = response.ClientSecret;
								try {
									const actionResult = await this.stripeElement.stripe.handleCardSetup(response.ClientSecret);
									if (!actionResult.error) {
										// Hide the error message now that the user has successfully authenticated
										this.cardErrorMessage = '';
										billingInfo.AuthorizationTokenCreateSetupIntent = 'False';
										this.updateBillingInfo();
										return;
									}
									else {
										this.cardErrorMessage = 'Card failed authentication.';
									}
								}
								catch (e) {
									// Exception when attempting to prompt user for authentication using Stripe
									this.cardErrorMessage = e ? (e.message || e.toString()) : 'Unknown error';
								}
							}
							else if (response.TaxIdInvalid) {
								this.taxIdInvalid = true;
							}
							// If there is an error other than requiring authentication, set the error message
							else {
								this.cardErrorMessage = response.Error;
							}
							break;
						case 'BillingAddressConfirm':
							this.confirmAddressModel = response;
							return;
						case 'BillingAddressError':
							this.$emit('set-errors', response.Errors);
							break;
						}
					}
				}
				catch (e) {
					// Exception when attempting to submit billing info to Cognito
					this.cardErrorMessage = e ? (e.message || e.toString()) : 'Unknown error';
				}
				if (updateBillingSuccess)
					this.$emit('upgrade-ready', this.subscriptionViewModelInternal);
				else
					return updateBillingSuccess;
			},
			async createStripePaymentMethod() {
				const stripeResponse = await this.stripeElement.stripe.createPaymentMethod('card', this.stripeElement.cardNumberElement, {
					billing_details: {
						name: `${this.subscriptionViewModelInternal.BillingInfo.FirstName} ${this.subscriptionViewModelInternal.BillingInfo.LastName}`,
						address: {
							line1: this.subscriptionViewModelInternal.BillingInfo.Line1,
							line2: this.subscriptionViewModelInternal.BillingInfo.Line2,
							city: this.subscriptionViewModelInternal.BillingInfo.City,
							state: this.subscriptionViewModelInternal.BillingInfo.State,
							postal_code: this.subscriptionViewModelInternal.BillingInfo.PostalCode,
							country: this.subscriptionViewModelInternal.BillingInfo.CountryCode
						}
					}
				});

				if (stripeResponse.error) {
					this.cardErrorMessage = stripeResponse.error.message;
					throw new Error(this.cardErrorMessage);
				}
				else {
					// Update the billing info with the new payment method
					this.subscriptionViewModelInternal.BillingInfo.AuthorizationTokenText = stripeResponse.paymentMethod.id;
					this.subscriptionViewModelInternal.BillingInfo.AuthorizationTokenType = 'PaymentMethod';
					this.subscriptionViewModelInternal.BillingInfo.AuthorizationTokenCreateSetupIntent = window.location.search.includes('disableUseSetupIntent') ? 'False' : 'True';
					this.subscriptionViewModelInternal.BillingInfo.ExpirationDate = `${stripeResponse.paymentMethod.card.exp_month}/${(stripeResponse.paymentMethod.card.exp_year).toString()}`;
					this.cardModified = false;
				}
			},
			async handleSubmit(shouldValidateAddress = true) {
				if (!this.validateBillingFields())
					throw new Error(this.cardErrorMessage || 'Please complete all required fields');

				const cardModified = this.cardModified;
				if (cardModified)
					await this.createStripePaymentMethod();

				if (cardModified || this.billingInfoChanged) {
					const updatedBillingInfo = await this.updateBillingInfo(shouldValidateAddress);
					if (updatedBillingInfo === false) {
						notify('Failed to update billing info.', 'error');
						throw new Error(this.cardErrorMessage);
					}
				}
				else {
					// card unchanged - change plan
					this.$emit('upgrade-ready', this.subscriptionViewModelInternal);
				}
			},
			handleAddressFieldBlur() {
				if (this.billingAddressEdited) {
					this.$emit('address-updated');
					Object.assign(this.latestCostAddress, this.subscriptionViewModelInternal.BillingInfo);
				}
			}
		}
	});

</script>

<style lang="scss">
	.billing-field-container {
		position: relative;
		height: 29.594px;
		background: var(--bg-input);

		.StripeElement--focus {

			&::before {
				position: absolute;
				bottom: 0;
				left: 0;
				right: 0;
				top: 0;
				outline: 1px solid var(--outline-focused);
				content: '';
			}
		}
	}

	input:focus {
		border-color: var(--outline-focused);
	}

	.checkout-billing--annual {

		.gap-2 {
			gap: var(--spacing-3);
		}

		.mb-2 {
			margin-bottom: var(--spacing-3);
		}
	}
</style>
